import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Heart, MapPin, Ruler, Bed, Bathtub, Calendar, Check, Buildings, UsersThree, Airplane, Storefront, Stack,
} from "@phosphor-icons/react";
import { useT } from "../i18n";
import { useRate } from "../context/RateContext";
import { useAuth } from "../context/AuthContext";
import { formatMAD, convertMad, monthlyPayment } from "../lib/format";
import api from "../lib/api";
import MonthlySimulator from "../components/MonthlySimulator";
import PanoramaViewer from "../components/PanoramaViewer";
import PropertyMap from "../components/PropertyMap";
import { ContactSection } from "./Home";
import PropertyCard from "../components/PropertyCard";
import { Reveal } from "../components/Reveal";
import { toast } from "sonner";

const PropertyDetail = () => {
  const { id } = useParams();
  const { t, lang } = useT();
  const rate = useRate();
  const { user, toggleFavorite } = useAuth();
  const navigate = useNavigate();
  const [prop, setProp] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [siblings, setSiblings] = useState([]);
  const [insights, setInsights] = useState(null);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    setInsights(null);
    setSiblings([]);
    api.get(`/properties/${id}`).then(({ data }) => {
      setProp(data);
      setActiveImg(0);
      api.get("/properties", { params: { type: data.type } }).then((r) => setSimilar(r.data.filter((p) => p.id !== data.id && p.project_slug !== data.project_slug).slice(0, 3)));
      if (data.project_slug) {
        api.get("/properties", { params: { project: data.project_slug } }).then((r) => setSiblings(r.data.filter((p) => p.id !== data.id)));
      }
      api.get(`/properties/${id}/insights`).then((r) => setInsights(r.data)).catch(() => {});
    }).catch(() => navigate("/biens"));
  }, [id, navigate]);

  if (!prop) return <div className="pt-40 pb-40 text-center text-muted-foreground">{t("common.loading")}</div>;

  const title = prop.title?.[lang] || prop.title?.fr;
  const desc = prop.description?.[lang] || prop.description?.fr;
  const conv = convertMad(prop.price_mad, rate, lang);
  const isFav = user && user.favorites && user.favorites.includes(prop.id);
  const monthly20 = monthlyPayment(prop.price_mad * 0.8, rate.interest_rate || 4.5, 20);
  const monthlyConv = convertMad(monthly20, rate, lang);

  const onFav = async () => {
    if (!user || !user.email) { navigate("/connexion"); return; }
    const res = await toggleFavorite(prop.id);
    toast.success(res.active ? "Ajouté à vos favoris" : "Retiré de vos favoris");
  };

  return (
    <div className="relative z-10 pt-[72px]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6">
        <button data-testid="prop-back" onClick={() => navigate("/biens")} className="inline-flex items-center gap-2 text-sm text-navy hover:text-gold transition-colors">
          <ArrowLeft size={18} className="rtl:rotate-180" /> {t("prop.back")}
        </button>
      </div>

      {/* Gallery */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.6fr_1fr] gap-4">
        <div className="relative aspect-[16/10] overflow-hidden bg-muted">
          <img src={prop.images?.[activeImg]} alt={title} className="w-full h-full object-cover" />
          <span className="absolute top-4 start-4 bg-navy/90 text-white text-xs font-semibold uppercase tracking-wider px-3 py-1.5 flex items-center gap-1.5">
            <Buildings size={14} weight="light" /> {t(`types.${prop.type}`)}
          </span>
          <button data-testid="prop-fav" onClick={onFav} className="absolute top-4 end-4 w-10 h-10 flex items-center justify-center bg-white/90 hover:bg-white transition-colors rounded-full">
            <Heart size={20} weight={isFav ? "fill" : "regular"} className={isFav ? "text-red-500" : "text-navy"} />
          </button>
        </div>
        <div className="grid grid-cols-3 lg:grid-cols-2 gap-4 content-start">
          {(prop.images || []).map((img, i) => (
            <button key={i} onClick={() => setActiveImg(i)} className={`aspect-square overflow-hidden border-2 transition-colors ${activeImg === i ? "border-gold" : "border-transparent"}`}>
              <img src={img} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 grid lg:grid-cols-[1.6fr_1fr] gap-12">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3"><MapPin size={16} weight="light" /> {prop.city}{prop.region ? `, ${prop.region}` : ""}</div>
          <h1 data-testid="prop-title" className="font-display text-3xl sm:text-4xl lg:text-5xl text-navy font-light tracking-tight mb-3">{title}</h1>
          {(prop.project_name || prop.ref) && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground mb-6">
              {prop.project_name && (
                <button data-testid="prop-project-link" onClick={() => navigate(`/biens?project=${prop.project_slug}`)} className="text-navy font-semibold hover:text-gold transition-colors">
                  {prop.project_name?.[lang] || prop.project_name?.fr}
                </button>
              )}
              {prop.ref && <span>· {t("prop.ref")} {prop.ref}</span>}
              {prop.floor && <span>· {prop.floor?.[lang] || prop.floor?.fr}</span>}
            </div>
          )}

          <div className="flex flex-wrap gap-6 py-6 border-y border-navy/10 mb-8">
            {prop.surface_m2 > 0 && <Spec icon={Ruler} label={t("prop.surface")} value={`${prop.surface_m2} m²`} />}
            {prop.bedrooms > 0 && <Spec icon={Bed} label={t("prop.bedrooms")} value={prop.bedrooms} />}
            {prop.bathrooms > 0 && <Spec icon={Bathtub} label={t("prop.bathrooms")} value={prop.bathrooms} />}
            {prop.delivery && <Spec icon={Calendar} label={t("prop.delivery")} value={prop.delivery} />}
          </div>

          <p className="text-muted-foreground leading-relaxed whitespace-pre-line mb-10">{desc}</p>

          {prop.features?.length > 0 && (
            <div className="mb-10">
              <h3 className="overline text-gold mb-5">{t("prop.features")}</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {prop.features.map((f) => (
                  <div key={f} className="flex items-center gap-3 text-navy">
                    <span className="w-5 h-5 bg-gold/15 flex items-center justify-center"><Check size={12} weight="bold" className="text-gold" /></span> {f}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar price */}
        <aside className="lg:sticky lg:top-24 h-fit">
          <div className="bg-white border border-navy/10 p-7">
            {prop.price_on_request || !prop.price_mad ? (
              <>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("prop.from")}</div>
                <div className="font-roboto font-bold text-2xl text-navy">{t("prop.on_request")}</div>
                <p className="mt-3 text-sm text-muted-foreground">{t("prop.in_project_sub")}</p>
              </>
            ) : (
              <>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("prop.from")}</div>
                <div className="font-roboto font-bold text-3xl text-navy">{formatMAD(prop.price_mad, lang)} <span className="text-lg text-muted-foreground font-sans font-normal">{t("common.mad")}</span></div>
                <div className="text-gold font-roboto font-bold text-lg">≈ {conv.text} {conv.symbol}</div>
                <div className="mt-4 pt-4 border-t border-navy/10 text-sm text-muted-foreground">
                  {t("prop.monthly_from")} <span className="font-roboto font-bold text-xl text-navy">{formatMAD(monthly20, lang)} {t("common.mad")}</span> {t("prop.per_month")}
                  <div className="text-gold font-roboto font-semibold text-sm">≈ {monthlyConv.text} {monthlyConv.symbol} {t("prop.per_month")}</div>
                  <div className="text-xs">(20 {t("sim.years")} · {(rate.interest_rate || 4.5).toFixed(1)}%)</div>
                </div>
              </>
            )}
            <button data-testid="prop-contact-cta" onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })} className="mt-6 w-full bg-navy text-white font-semibold py-4 rounded-full hover:bg-[#254B75] transition-colors">
              {t("contact.request_info")}
            </button>
            <button data-testid="prop-fav-side" onClick={onFav} className="mt-3 w-full border border-navy/20 text-navy font-semibold py-3 rounded-full hover:border-gold transition-colors inline-flex items-center justify-center gap-2">
              <Heart size={18} weight={isFav ? "fill" : "regular"} className={isFav ? "text-red-500" : ""} /> {isFav ? "★" : t("dash.favorites")}
            </button>
          </div>
        </aside>
      </div>

      {/* Floor plan + available in this project */}
      <ProjectAndPlan prop={prop} siblings={siblings} t={t} lang={lang} navigate={navigate} />

      {/* 360 tour + map */}
      <section data-testid="tour-map-section" className="bg-background relative z-10 py-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 space-y-14">
          <div>
            <span className="overline text-gold">Immersion</span>
            <h2 className="font-display text-2xl sm:text-3xl text-navy font-light mt-2 mb-6">Visite virtuelle 360°</h2>
            <PanoramaViewer tourUrl={prop.tour_url} panoramaUrl={prop.panorama_url} />
          </div>
          <div>
            <span className="overline text-gold">Emplacement</span>
            <h2 className="font-display text-2xl sm:text-3xl text-navy font-light mt-2 mb-6">Le quartier & les points d'intérêt</h2>
            {insights && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-testid="property-insights">
                <InsightCard icon={UsersThree} value={insights.facts.population ? insights.facts.population.toLocaleString("fr-FR") : "—"} label="Habitants (ville)" />
                <InsightCard icon={Buildings} value={insights.facts.companies ? insights.facts.companies.toLocaleString("fr-FR") + "+" : "—"} label="Entreprises" />
                <InsightCard icon={Airplane} value={insights.airport_km ? insights.airport_km + " km" : "—"} label={insights.facts.airport ? "Aéroport " + insights.facts.airport : "Aéroport"} />
                <InsightCard icon={Storefront} value={Object.entries(insights.surroundings).filter(([k]) => k !== "airport").reduce((a, [, v]) => a + v, 0) + "+"} label="Commodités autour" />
              </div>
            )}
            <PropertyMap property={prop} />
          </div>
        </div>
      </section>

      {/* Embedded simulator */}
      <section className="bg-[#0B111A] relative z-10 py-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <h2 className="font-display text-2xl sm:text-3xl text-white font-light mb-8">{t("sim.title")}</h2>
          <MonthlySimulator embedded initialPrice={prop.price_mad} />
        </div>
      </section>

      {similar.length > 0 && (
        <section className="bg-background relative z-10 py-20">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <h2 className="font-display text-2xl sm:text-3xl text-navy font-light mb-8">{t("prop.similar")}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {similar.map((p) => <PropertyCard key={p.id} property={p} />)}
            </div>
          </div>
        </section>
      )}

      <ContactSection propertyId={prop.id} defaultSubject="property" />
    </div>
  );
};

const ProjectAndPlan = ({ prop, siblings, t, lang, navigate }) => {
  const hasPlan = !!prop.floor_plan;
  if (!hasPlan && siblings.length === 0) return null;
  const preview = siblings.slice(0, 6);
  return (
    <section data-testid="project-section" className="bg-white relative z-10 py-20 border-t border-navy/5">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        {hasPlan && (
          <div className="mb-16">
            <span className="overline text-gold">{t("prop.plans_title")}</span>
            <h2 className="font-display text-2xl sm:text-3xl text-navy font-light mt-2 mb-6">{prop.floor?.[lang] || prop.floor?.fr || t("prop.plans_title")}</h2>
            <div className="border border-navy/10 rounded-2xl overflow-hidden bg-background max-w-4xl">
              <img data-testid="plan-image" src={prop.floor_plan} alt="" className="w-full h-auto object-contain" />
            </div>
          </div>
        )}
        {siblings.length > 0 && (
          <div>
            <div className="flex items-center gap-2.5 mb-2">
              <Stack size={22} weight="light" className="text-gold" />
              <span className="overline text-gold">{t("prop.in_project")}</span>
            </div>
            <h2 className="font-display text-2xl sm:text-3xl text-navy font-light mb-1">{prop.project_name?.[lang] || prop.project_name?.fr}</h2>
            <p className="text-sm text-muted-foreground mb-8 max-w-2xl">{t("prop.in_project_sub")}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="project-siblings">
              {preview.map((p) => <PropertyCard key={p.id} property={p} />)}
            </div>
            <button data-testid="view-all-project" onClick={() => navigate(`/biens?project=${prop.project_slug}`)} className="mt-8 inline-flex items-center gap-2 bg-navy text-white font-semibold px-6 py-3 rounded-full hover:bg-[#254B75] transition-colors">
              {t("prop.view_all_project")} ({siblings.length})
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

const InsightCard = ({ icon: Icon, value, label }) => (
  <div className="bg-white border border-navy/10 rounded-2xl p-5 text-center">
    <Icon size={28} weight="light" className="text-gold mx-auto mb-2" />
    <div className="font-roboto font-bold text-2xl text-navy leading-none">{value}</div>
    <div className="text-xs text-muted-foreground mt-1.5">{label}</div>
  </div>
);

const Spec = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3">
    <Icon size={26} weight="light" className="text-gold" />
    <div>
      <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className="font-roboto font-bold text-lg text-navy">{value}</div>
    </div>
  </div>
);

export default PropertyDetail;
